import React, { useState, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import {
    X, Mail, Phone, MapPin, Calendar, CreditCard, AlertTriangle, CheckCircle2,
    Clock, MessageSquare, Building2, RotateCw, Pencil, User, FileText,
    Shield, ShieldAlert, ShieldCheck, XCircle, AlertCircle, Loader2, RefreshCw, Bot, Info,
    Send, FileCheck,
} from 'lucide-react';
import {
    apiClient, Driver, DriverEmailHistoryResponse, UpdateDriverRequest, FleetSubtype,
    LicenceCheckResult, ClearToDriveBlocker, DriverIssue, ConsentStatusResponse,
} from '../../services/api';
import EditDriverModal from './EditDriverModal';
import DriverDocumentsTab from '../DriverDocumentsTab';
import DbsTab from '../DBS/DbsTab';

type Tab = 'driver' | 'licence' | 'documents' | 'history' | 'consent' | 'dbs' | 'communications';

interface DriverViewModalProps {
    driver: Driver | null;
    isOpen: boolean;
    onClose: () => void;
    onDriverUpdated?: () => void;
    subtype?: FleetSubtype;
    initialTab?: Tab;
    user?: any;
}

const Row = ({ icon, label, value }: { icon: React.ReactNode; label: React.ReactNode; value: React.ReactNode }) => (
    <div className="flex items-start gap-3">
        <div className="flex-shrink-0 mt-0.5">{icon}</div>
        <div className="min-w-0">
            <p className="text-xs text-gray-500">{label}</p>
            <div className="text-sm font-medium text-gray-900 mt-0.5 break-words">{value}</div>
        </div>
    </div>
);

const DriverViewModal: React.FC<DriverViewModalProps> = ({
    driver,
    isOpen,
    onClose,
    onDriverUpdated,
    subtype = 'general',
    initialTab,
    user,
}) => {
    const orgName: string = user?.organisation?.name ?? 'Your Organisation';
    const isAdmin: boolean = user?.role === 'admin' || user?.role === 'super_admin';
    const currentUserName: string = [user?.firstName, user?.lastName].filter(Boolean).join(' ') || user?.email || '';
    const [activeTab, setActiveTab] = useState<Tab>(initialTab ?? 'driver');

    // Email state
    const [emailHistory, setEmailHistory] = useState<DriverEmailHistoryResponse | null>(null);
    const [isLoadingEmails, setIsLoadingEmails] = useState(false);
    const [emailError, setEmailError] = useState<string | null>(null);

    // Driver state
    const [isReactivating, setIsReactivating] = useState(false);
    const [showEditDriverModal, setShowEditDriverModal] = useState(false);
    const [isUpdatingDriver, setIsUpdatingDriver] = useState(false);

    // Licence check state
    const [isChecking, setIsChecking] = useState(false);
    const isCheckingRef = useRef(false);
    const [checkResult, setCheckResult] = useState<LicenceCheckResult | null>(null);
    const [checkError, setCheckError] = useState<string | null>(null);
    const [isLoadingLatest, setIsLoadingLatest] = useState(false);
    const [, setShowHistory] = useState(false);
    const [checkHistory, setCheckHistory] = useState<LicenceCheckResult[]>([]);
    const [isLoadingHistory, setIsLoadingHistory] = useState(false);
    const [clearToDriveData, setClearToDriveData] = useState<{
        clearToDrive?: boolean;
        issues?: DriverIssue[];
        clearToDriveBlockers?: ClearToDriveBlocker[];
    } | null>(null);
    const [showRecentCheckConfirm, setShowRecentCheckConfirm] = useState(false);

    // History tab state
    const [historyFilter, setHistoryFilter] = useState<'all' | 'licence' | 'consent'>('all');
    const [consentTokens, setConsentTokens] = useState<ConsentStatusResponse['tokens'] | null>(null);
    const [isLoadingConsentTokens, setIsLoadingConsentTokens] = useState(false);

    // Consent tab state
    const [isWithdrawingConsent, setIsWithdrawingConsent] = useState(false);
    const [isSendingConsent, setIsSendingConsent] = useState(false);
    const [isSendingReminder, setIsSendingReminder] = useState(false);
    const [showRecordOffline, setShowRecordOffline] = useState(false);
    const [consentReference, setConsentReference] = useState('');
    const [isRecordingConsent, setIsRecordingConsent] = useState(false);

    useEffect(() => {
        if (isOpen) {
            setActiveTab(initialTab ?? 'driver');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, initialTab]);

    useEffect(() => {
        if (isOpen && driver && activeTab === 'licence') {
            loadLatestCheck();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, driver?.id, activeTab]);

    const handleClose = () => {
        setActiveTab('driver');
        setEmailHistory(null);
        setEmailError(null);
        setCheckResult(null);
        setCheckError(null);
        setClearToDriveData(null);
        setShowHistory(false);
        setCheckHistory([]);
        setShowRecentCheckConfirm(false);
        setShowEditDriverModal(false);
        setShowRecordOffline(false);
        setConsentReference('');
        setHistoryFilter('all');
        setConsentTokens(null);
        onClose();
    };

    if (!isOpen || !driver) return null;

    // ── Formatting helpers ────────────────────────────────────────────────────

    const getRiskBadgeClass = (risk?: string) => {
        switch (risk) {
            case 'high':   return 'bg-red-100 text-red-800 border-red-200';
            case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
            case 'low':    return 'bg-green-100 text-green-800 border-green-200';
            default:       return 'bg-gray-100 text-gray-800 border-gray-200';
        }
    };

    const formatDate = (dateString?: string | null) => {
        if (!dateString) return 'Not provided';
        return new Date(dateString).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });
    };

    const formatDateTime = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-GB', {
            day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit',
        });
    };

    const calculateAge = (dob: string) => {
        const today = new Date();
        const birth = new Date(dob);
        let age = today.getFullYear() - birth.getFullYear();
        const m = today.getMonth() - birth.getMonth();
        if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
        return age;
    };

    const issueIcon = (iconType: string, severity: string) => {
        const cls = severity === 'critical' ? 'text-red-500' : severity === 'warning' ? 'text-amber-500' : 'text-blue-500';
        if (iconType === 'error' || iconType === 'ban') return <XCircle className={`w-4 h-4 ${cls} flex-shrink-0`} />;
        if (iconType === 'warning' || iconType === 'clock') return <AlertTriangle className={`w-4 h-4 ${cls} flex-shrink-0`} />;
        return <Info className={`w-4 h-4 ${cls} flex-shrink-0`} />;
    };

    const getHistoryStatusBar = (status: string, valid: boolean): { barClass: string; label: string } => {
        const statusLower = status?.toLowerCase() || '';
        const titleCase = (s: string) => s.replace(/\b\w/g, c => c.toUpperCase());
        const isInvalid = !valid || statusLower.includes('expired') || statusLower.includes('revoked') ||
            statusLower.includes('disqualified') || statusLower.includes('invalid');
        if (isInvalid) return { barClass: 'bg-red-500', label: status ? titleCase(status) : 'Invalid' };
        if (statusLower.includes('provisional')) return { barClass: 'bg-amber-400', label: titleCase(status) };
        return { barClass: 'bg-green-500', label: titleCase(status) };
    };

    const getConsentStatusBar = (status: string): { barClass: string; label: string } => {
        const s = status?.toLowerCase() || '';
        const label = s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
        if (s === 'active') return { barClass: 'bg-green-500', label: 'Active' };
        if (s === 'sent' || s === 'opened') return { barClass: 'bg-amber-400', label };
        if (s === 'expired' || s === 'declined') return { barClass: 'bg-red-500', label };
        return { barClass: 'bg-gray-300', label };
    };

    const getHistoryRiskBar = (level: string): { barClass: string; label: string } => {
        const key = level?.toLowerCase() || '';
        const barClass = key === 'low' ? 'bg-green-500' : key === 'medium' ? 'bg-amber-400' : key === 'high' ? 'bg-red-500' : 'bg-gray-300';
        return { barClass, label: key ? key.charAt(0).toUpperCase() + key.slice(1) : 'Unknown' };
    };

    const isConsentExpired = () => {
        if (!driver.consentProvided || !driver.consentExpiry) return false;
        return new Date(driver.consentExpiry) <= new Date();
    };

    const isRecentCheck = () =>
        !!(driver.lastCheckDate &&
            driver.lastCheckWasSuccessful === true &&
            driver.daysSinceLastCheck !== null &&
            driver.daysSinceLastCheck !== undefined &&
            driver.daysSinceLastCheck <= 7);

    const loadLatestCheck = async () => {
        try {
            setIsLoadingLatest(true);
            setCheckError(null);
            const result = await apiClient.getLatestLicenceCheck(driver.id);
            if (result.data.driver) {
                setClearToDriveData({
                    clearToDrive: result.data.driver.clearToDrive,
                    issues: result.data.driver.issues,
                    clearToDriveBlockers: result.data.driver.clearToDriveBlockers,
                });
            }
            if (result.data.latestCheck) {
                setCheckResult(result.data.latestCheck);
            }
        } catch (err: any) {
            if (!err.message?.includes('not found')) {
                console.error('Error loading latest check:', err);
            }
            setClearToDriveData({
                clearToDrive: driver.clearToDrive,
                issues: driver.issues,
                clearToDriveBlockers: driver.issues?.filter(i => i.severity === 'critical').map(i => ({
                    code: i.code, label: i.label, description: i.filterLabel || '',
                })),
            });
        } finally {
            setIsLoadingLatest(false);
        }
    };

    const loadCheckHistory = async () => {
        try {
            setIsLoadingHistory(true);
            const result = await apiClient.getLicenceCheckHistory(driver.id);
            setCheckHistory(result.data || []);
        } catch (err: any) {
            console.error('Error loading check history:', err);
        } finally {
            setIsLoadingHistory(false);
        }
    };

    const loadConsentTokens = async () => {
        try {
            setIsLoadingConsentTokens(true);
            const result = await apiClient.getConsentStatus(driver.id);
            setConsentTokens(result.tokens || []);
        } catch (err) {
            console.error('Error loading consent tokens:', err);
            setConsentTokens([]);
        } finally {
            setIsLoadingConsentTokens(false);
        }
    };

    const handleCheckButtonClick = () => {
        if (!driver.consentProvided) {
            setCheckError('Driver consent is required before performing licence checks. Please record D906 consent first.');
            return;
        }
        if (isConsentExpired()) {
            setCheckError('Driver consent has expired. Please renew D906 consent before performing licence checks.');
            return;
        }
        if (isRecentCheck()) {
            setShowRecentCheckConfirm(true);
            return;
        }
        performLicenceCheck();
    };

    const performLicenceCheck = async () => {
        if (isCheckingRef.current) return;
        setShowRecentCheckConfirm(false);
        try {
            isCheckingRef.current = true;
            setIsChecking(true);
            setCheckError(null);
            const result = await apiClient.performLicenceCheck(driver.id);
            setCheckResult(result.data);
            await loadLatestCheck();
            if (onDriverUpdated) onDriverUpdated();
        } catch (err: any) {
            setCheckError(err.message || 'Failed to perform licence check');
        } finally {
            isCheckingRef.current = false;
            setIsChecking(false);
        }
    };

    // ── Email helpers ─────────────────────────────────────────────────────────

    const loadEmailHistory = async () => {
        setIsLoadingEmails(true);
        setEmailError(null);
        try {
            const response = await apiClient.getDriverEmailHistory(driver.id);
            setEmailHistory(response);
        } catch (error: any) {
            setEmailError(error.message || 'Failed to load email history');
        } finally {
            setIsLoadingEmails(false);
        }
    };

    const handleWithdrawConsent = async () => {
        const reason = prompt('Please provide a reason for withdrawing consent:');
        if (!reason) return;
        try {
            setIsWithdrawingConsent(true);
            await apiClient.adminWithdrawConsent(driver.id, reason);
            toast.success('Consent withdrawn successfully');
            if (onDriverUpdated) onDriverUpdated();
        } catch (err: any) {
            toast.error('Failed to withdraw consent: ' + err.message);
        } finally {
            setIsWithdrawingConsent(false);
        }
    };

    const handleSendDigitalConsent = async () => {
        try {
            setIsSendingConsent(true);
            await apiClient.sendConsentRequest({ driverId: driver.id });
            toast.success('Consent request sent successfully');
            if (onDriverUpdated) onDriverUpdated();
        } catch (err: any) {
            toast.error('Failed to send consent request: ' + err.message);
        } finally {
            setIsSendingConsent(false);
        }
    };

    const handleSendReminder = async () => {
        try {
            setIsSendingReminder(true);
            await apiClient.sendConsentReminder(driver.id);
            toast.success('Reminder sent successfully');
            if (onDriverUpdated) onDriverUpdated();
        } catch (err: any) {
            toast.error('Failed to send reminder: ' + err.message);
        } finally {
            setIsSendingReminder(false);
        }
    };

    const handleSubmitOfflineConsent = async () => {
        if (!consentReference.trim()) return;
        try {
            setIsRecordingConsent(true);
            await apiClient.recordDriverConsent(driver.id, {
                consentReference: consentReference.trim(),
                consentDate: new Date().toISOString(),
                consentMethod: 'offline'
            });
            toast.success('Offline consent recorded successfully');
            setShowRecordOffline(false);
            setConsentReference('');
            if (onDriverUpdated) onDriverUpdated();
        } catch (err: any) {
            toast.error('Failed to record consent: ' + err.message);
        } finally {
            setIsRecordingConsent(false);
        }
    };

    const handleTabChange = (tab: Tab) => {
        setActiveTab(tab);
        if (tab === 'communications' && !emailHistory && !isLoadingEmails) {
            loadEmailHistory();
        }
        if (tab === 'history') {
            if (checkHistory.length === 0 && !isLoadingHistory) loadCheckHistory();
            if (consentTokens === null && !isLoadingConsentTokens) loadConsentTokens();
        }
    };

    const handleReactivateDriver = async () => {
        if (!window.confirm(`Reactivate ${driver.firstName} ${driver.lastName}? They will need to provide consent again.`)) return;
        try {
            setIsReactivating(true);
            const response = await apiClient.reactivateDriver(driver.id);
            toast.success(response.message || 'Driver reactivated successfully');
            if (onDriverUpdated) onDriverUpdated();
            handleClose();
        } catch (error: any) {
            toast.error('Failed to reactivate driver: ' + error.message);
        } finally {
            setIsReactivating(false);
        }
    };

    const handleEditDriverSubmit = async (driverId: string, updatedDriver: UpdateDriverRequest) => {
        try {
            setIsUpdatingDriver(true);
            await apiClient.updateDriver(driverId, updatedDriver);
            toast.success('Driver updated successfully!');
            if (onDriverUpdated) onDriverUpdated();
            setShowEditDriverModal(false);
        } catch (error: any) {
            toast.error('Failed to update driver: ' + error.message);
            throw error;
        } finally {
            setIsUpdatingDriver(false);
        }
    };

    // ── Subtype-aware category data ───────────────────────────────────────────

    const pcvCodes = ['D', 'D1', 'DE', 'D1E'];
    const hgvCodes = ['C', 'C1', 'CE', 'C1E'];
    const categoryNames: Record<string, string> = {
        'C': 'HGV (Rigid)', 'C1': 'Medium HGV', 'CE': 'HGV + Trailer', 'C1E': 'Medium HGV + Trailer',
        'D': 'Bus/Coach', 'D1': 'Minibus', 'DE': 'Bus + Trailer', 'D1E': 'Minibus + Trailer',
        'B': 'Car', 'BE': 'Car + Trailer', 'B1': 'Light vehicle',
        'A': 'Motorcycle', 'A1': 'Light motorcycle', 'A2': 'Medium motorcycle', 'AM': 'Moped',
        'F': 'Tractor', 'G': 'Road roller', 'H': 'Track vehicle', 'K': 'Mowing machine',
        'L': 'Electric vehicle', 'N': 'Exempt vehicle', 'P': 'Moped', 'Q': 'Motor vehicle',
    };

    const getRelevantProfessionalCodes = () => {
        if (subtype === 'bus' || subtype === 'coach') return pcvCodes;
        if (subtype === 'haulage') return hgvCodes;
        if (subtype === 'car_fleet') return [];
        return [...pcvCodes, ...hgvCodes];
    };

    const getProfessionalLabel = () => {
        if (subtype === 'bus' || subtype === 'coach') return 'PCV';
        if (subtype === 'haulage') return 'HGV';
        return 'Professional';
    };

    const canViewDocuments = ['admin', 'manager', 'compliance', 'super_admin'].includes(user?.role || '');
    const tabs: { id: Tab; label: string }[] = [
        { id: 'driver',         label: 'Driver'         },
        { id: 'licence',        label: 'Licence'        },
        ...(canViewDocuments ? [{ id: 'documents' as Tab, label: 'Documents' }] : []),
        { id: 'consent',        label: 'Consent'        },
        ...(user?.organisation?.trustidEnabled ? [{ id: 'dbs' as Tab, label: 'DBS' }] : []),
        { id: 'history',        label: 'History'        },
        { id: 'communications', label: 'Communications' },
    ];

    const cardClass = 'bg-gray-50/80 rounded-lg border border-gray-100 p-4';

    return (
        <>
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
                <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full flex flex-col" style={{ height: '75vh' }}>

                    {/* Header */}
                    <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
                        <div className="flex items-center gap-4 min-w-0 flex-1">
                            <div className="w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'rgba(105,190,40,0.1)' }}>
                                <span className="text-base font-bold" style={{ color: '#69BE28' }}>
                                    {driver.firstName.charAt(0)}{driver.lastName.charAt(0)}
                                </span>
                            </div>
                            <div className="min-w-0">
                                <h2 className="text-base font-semibold text-gray-900 truncate">{driver.firstName} {driver.lastName}</h2>
                                <p className="text-xs text-gray-500 truncate">{driver.email}</p>
                                <div className="flex items-center gap-2 mt-1.5">
                                    <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full border ${getRiskBadgeClass(driver.riskLevel)}`}>
                                        {(driver.riskLevel || 'Unknown').charAt(0).toUpperCase() + (driver.riskLevel || 'Unknown').slice(1)} Risk
                                    </span>
                                    <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full border ${driver.active ? 'bg-green-100 text-green-800 border-green-200' : 'bg-red-100 text-red-800 border-red-200'}`}>
                                        {driver.active ? 'Active' : 'Inactive'}
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div className="flex items-center gap-1 ml-3 flex-shrink-0">
                            {driver.active && (
                                <button
                                    onClick={() => setShowEditDriverModal(true)}
                                    className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
                                    title="Edit Driver"
                                >
                                    <Pencil className="w-4 h-4" />
                                </button>
                            )}
                            <button
                                onClick={handleClose}
                                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                    </div>

                    {/* Tab bar */}
                    <div className="flex overflow-x-auto border-b border-gray-200 px-6 flex-shrink-0">
                        {tabs.map((tab) => (
                            <button
                                key={tab.id}
                                onClick={() => handleTabChange(tab.id)}
                                className={`px-3 py-3 text-sm font-medium border-b-2 transition-colors -mb-px whitespace-nowrap ${
                                    activeTab === tab.id
                                        ? 'border-green-500 text-green-700'
                                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                }`}
                                style={activeTab === tab.id ? { borderColor: '#69BE28', color: '#4a8a1c' } : {}}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>

                    {/* Tab content - scrollable */}
                    <div className="flex-1 overflow-y-auto">

                        {/* ── Driver ── */}
                        {activeTab === 'driver' && (
                            <div className="p-6 grid grid-cols-1 lg:grid-cols-2 gap-4">

                                <div className={`${cardClass} space-y-3`}>
                                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Personal</h3>
                                    <Row icon={<Calendar className="w-4 h-4 text-gray-400" />} label="Date of Birth" value={`${formatDate(driver.dateOfBirth)} (Age ${calculateAge(driver.dateOfBirth)})`} />
                                    <Row icon={<Mail className="w-4 h-4 text-gray-400" />} label="Email" value={driver.email} />
                                    {driver.phone && <Row icon={<Phone className="w-4 h-4 text-gray-400" />} label="Phone" value={driver.phone} />}
                                </div>

                                <div className={`${cardClass} space-y-3`}>
                                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Address</h3>
                                    {driver.addressLine1 || driver.city ? (
                                        <div className="flex items-start gap-3">
                                            <MapPin className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                                            <div className="text-sm font-medium text-gray-900">
                                                {driver.addressLine1 && <p>{driver.addressLine1}</p>}
                                                {driver.addressLine2 && <p>{driver.addressLine2}</p>}
                                                {(driver.city || driver.postcode) && <p>{[driver.city, driver.postcode].filter(Boolean).join(', ')}</p>}
                                            </div>
                                        </div>
                                    ) : (
                                        <p className="text-sm text-gray-400 italic">No address provided</p>
                                    )}
                                </div>

                                <div className={`${cardClass} space-y-3`}>
                                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Site</h3>
                                    {driver.site ? (
                                        <Row
                                            icon={<Building2 className="w-4 h-4 text-gray-400" />}
                                            label={driver.site.name}
                                            value={`${driver.site.code}${driver.site.city ? ' · ' + driver.site.city : ''}`}
                                        />
                                    ) : (
                                        <p className="text-sm text-gray-400 italic">Unassigned</p>
                                    )}
                                </div>

                                <div className={`${cardClass} space-y-3`}>
                                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Account</h3>
                                    <Row icon={<Calendar className="w-4 h-4 text-gray-400" />} label="Created" value={formatDate(driver.createdAt)} />
                                    <Row icon={<Clock className="w-4 h-4 text-gray-400" />} label="Last Updated" value={formatDate(driver.updatedAt)} />
                                </div>

                                {!driver.active && (
                                    <div className="lg:col-span-2 bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
                                        <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                                        <div className="flex-1">
                                            <p className="text-sm font-medium text-amber-800">Driver Inactive</p>
                                            <p className="text-xs text-amber-700 mt-0.5">This driver has been deactivated. Reactivate them to enable operations. They will need to provide consent again.</p>
                                        </div>
                                        <button
                                            onClick={handleReactivateDriver}
                                            disabled={isReactivating}
                                            className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-white rounded-lg hover:opacity-90 disabled:opacity-50 transition-opacity flex-shrink-0"
                                            style={{ backgroundColor: '#69BE28' }}
                                        >
                                            {isReactivating
                                                ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                                : <RotateCw className="w-4 h-4" />
                                            }
                                            {isReactivating ? 'Reactivating...' : 'Reactivate'}
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* ── Licence ── */}
                        {activeTab === 'licence' && (
                            <div className="p-6 space-y-4">

                                {/* Always-visible summary cards */}
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                    <div className={`${cardClass} space-y-3`}>
                                        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Licence Details</h3>
                                        <Row
                                            icon={<CreditCard className="w-4 h-4 text-gray-400" />}
                                            label="Licence Number"
                                            value={<span className="font-mono bg-white border border-gray-200 rounded px-1.5 py-0.5 text-xs">{driver.licenceNumber}</span>}
                                        />
                                        <Row
                                            icon={<Shield className="w-4 h-4 text-gray-400" />}
                                            label={<>Risk Level <span className="normal-case font-normal text-gray-400">(stored on record)</span></>}
                                            value={
                                                <span className={`inline-flex mt-0.5 px-2 py-0.5 text-xs font-medium rounded-full border ${getRiskBadgeClass(driver.riskLevel)}`}>
                                                    {(driver.riskLevel || 'Not assessed').charAt(0).toUpperCase() + (driver.riskLevel || 'Not assessed').slice(1)}
                                                </span>
                                            }
                                        />
                                        <Row
                                            icon={<Clock className="w-4 h-4 text-gray-400" />}
                                            label="Last Check"
                                            value={driver.lastLicenceCheck ? formatDate(driver.lastLicenceCheck) : 'Never checked'}
                                        />
                                        {driver.daysSinceLastCheck != null && (
                                            <Row icon={<Calendar className="w-4 h-4 text-gray-400" />} label="Days Since Check" value={`${driver.daysSinceLastCheck} days`} />
                                        )}
                                        <Row
                                            icon={<Calendar className="w-4 h-4 text-gray-400" />}
                                            label="Licence Expiry"
                                            value={driver.licenceExpiryDate ? formatDate(driver.licenceExpiryDate) : 'Not provided'}
                                        />
                                        {driver.cpcExpiryDate && (
                                            <Row icon={<Calendar className="w-4 h-4 text-gray-400" />} label="CPC Expiry" value={formatDate(driver.cpcExpiryDate)} />
                                        )}
                                    </div>

                                    <div className={`${cardClass} space-y-3`}>
                                        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Compliance Status</h3>
                                        {(() => {
                                            const ctd = clearToDriveData?.clearToDrive ?? driver.clearToDrive;
                                            const issues = clearToDriveData?.issues || driver.issues;
                                            return (
                                                <>
                                                    {/* Clear-to-drive status */}
                                                    <div className={`flex items-center gap-2 px-3 py-2.5 rounded-lg ${
                                                        ctd === true  ? 'bg-green-600' :
                                                        ctd === false ? 'bg-red-600' :
                                                                        'bg-gray-200'
                                                    }`}>
                                                        {ctd === true  && <ShieldCheck className="w-4 h-4 text-white flex-shrink-0" />}
                                                        {ctd === false && <ShieldAlert className="w-4 h-4 text-white flex-shrink-0" />}
                                                        {ctd == null   && <Clock className="w-4 h-4 text-gray-500 flex-shrink-0" />}
                                                        <span className={`text-sm font-bold ${ctd == null ? 'text-gray-600' : 'text-white'}`}>
                                                            {ctd === true  ? 'Clear to Drive' :
                                                             ctd === false ? 'Not Clear to Drive' :
                                                                             'Status Unknown'}
                                                        </span>
                                                    </div>

                                                    {/* Issues list */}
                                                    {issues && issues.length > 0 ? (
                                                        <div className="space-y-1.5">
                                                            {issues.map((issue) => (
                                                                <div key={issue.code} className={`flex items-start gap-2.5 p-2 rounded-lg border ${
                                                                    issue.severity === 'critical' ? 'bg-red-50 border-red-100' :
                                                                    issue.severity === 'warning'  ? 'bg-amber-50 border-amber-100' :
                                                                                                    'bg-blue-50 border-blue-100'
                                                                }`}>
                                                                    {issueIcon(issue.icon, issue.severity)}
                                                                    <div>
                                                                        <p className="text-xs font-medium text-gray-900">{issue.label}</p>
                                                                        {issue.description && <p className="text-xs text-gray-500 mt-0.5">{issue.description}</p>}
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    ) : ctd === true ? (
                                                        <p className="text-xs text-gray-400 italic">No issues found</p>
                                                    ) : null}
                                                </>
                                            );
                                        })()}
                                    </div>
                                </div>


                                {/* Consent warnings */}
                                {!driver.consentProvided && (
                                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-center gap-3">
                                        <AlertTriangle className="w-5 h-5 text-yellow-500 flex-shrink-0" />
                                        <div>
                                            <p className="text-sm font-medium text-yellow-800">Consent Required</p>
                                            <p className="text-sm text-yellow-700">D906 consent must be recorded before performing licence checks.</p>
                                        </div>
                                    </div>
                                )}

                                {driver.consentProvided && isConsentExpired() && (
                                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-center gap-3">
                                        <AlertTriangle className="w-5 h-5 text-yellow-500 flex-shrink-0" />
                                        <div>
                                            <p className="text-sm font-medium text-yellow-800">Consent Expired</p>
                                            <p className="text-sm text-yellow-700">D906 consent has expired. Please renew before performing licence checks.</p>
                                        </div>
                                    </div>
                                )}

                                {/* Error */}
                                {checkError && (
                                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
                                        <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                                        <span className="text-sm text-red-800">{checkError}</span>
                                    </div>
                                )}

                                {/* Loading */}
                                {isLoadingLatest && (
                                    <div className="flex flex-col items-center justify-center py-10">
                                        <Loader2 className="w-6 h-6 animate-spin mb-2" style={{ color: '#69BE28' }} />
                                        <p className="text-sm text-gray-500">Loading latest check result...</p>
                                    </div>
                                )}

                                {/* No previous check empty state */}
                                {!isLoadingLatest && !checkResult && (
                                    <div className="flex flex-col items-center justify-center py-10 text-center">
                                        <div className="p-4 rounded-2xl mb-4" style={{ backgroundColor: 'rgba(105,190,40,0.08)' }}>
                                            <Shield className="w-10 h-10" style={{ color: '#69BE28' }} />
                                        </div>
                                        <p className="text-base font-semibold text-gray-900">No Previous Checks</p>
                                        <p className="text-sm text-gray-500 mt-2 max-w-xs">
                                            This driver hasn't had their licence checked yet.
                                            {driver.consentProvided && ' Click "Perform New Check" to validate their DVLA licence status.'}
                                        </p>
                                    </div>
                                )}

                                {/* Check results */}
                                {checkResult && (
                                    <div className="space-y-4">

                                        {/* Licence Status */}
                                        <div className="bg-gray-50/80 rounded-lg border border-gray-100 p-4">
                                            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Licence Status</h3>

                                            <div className="grid grid-cols-3 gap-4">
                                                <div>
                                                    <label className="block text-xs font-medium text-gray-500 mb-1">Validity</label>
                                                    {(() => { const sb = getHistoryStatusBar(checkResult.status, checkResult.valid); return <span className="inline-flex items-center gap-2 whitespace-nowrap text-xs font-semibold text-gray-700"><span className={`h-4 w-[3px] rounded-full ${sb.barClass}`} aria-hidden="true" />{sb.label}</span>; })()}
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-medium text-gray-500 mb-1">Licence Type</label>
                                                    <div className="text-sm font-medium text-gray-900">{checkResult.licenceType || 'Not specified'}</div>
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-medium text-gray-500 mb-1">Penalty Points</label>
                                                    {(checkResult.status?.toLowerCase().includes('disqualified') || checkResult.status?.toLowerCase().includes('revoked')) ? (
                                                        <span className="text-2xl font-bold text-red-600">N/A</span>
                                                    ) : (() => {
                                                        const pts = (checkResult.endorsements || [])
                                                            .filter((e: any) => !e.penaltyPointsExpiryDate || new Date(e.penaltyPointsExpiryDate) > new Date())
                                                            .reduce((sum: number, e: any) => sum + (e.penaltyPoints || 0), 0);
                                                        return <span className={`text-2xl font-bold ${pts >= 9 ? 'text-red-600' : pts >= 6 ? 'text-yellow-600' : 'text-green-600'}`}>{pts}</span>;
                                                    })()}
                                                </div>
                                            </div>

                                            <div className="pt-3 border-t border-gray-100 mt-3">
                                                <label className="block text-xs font-medium text-gray-500 mb-2">Categories</label>
                                                {checkResult.categories.length > 0 ? (() => {
                                                    const now = new Date();
                                                    const relevantProfCodes = getRelevantProfessionalCodes();
                                                    const sorted = [...checkResult.categories].sort((a, b) => {
                                                        const isProfA = relevantProfCodes.includes(a.categoryCode);
                                                        const isProfB = relevantProfCodes.includes(b.categoryCode);
                                                        if (isProfA && !isProfB) return -1;
                                                        if (!isProfA && isProfB) return 1;
                                                        return 0;
                                                    });
                                                    const professionalCats = sorted.filter(c => relevantProfCodes.includes(c.categoryCode));
                                                    const standardCats = sorted.filter(c => !relevantProfCodes.includes(c.categoryCode));

                                                    const renderCategoryBadge = (cat: any, idx: number, isProfessional: boolean) => {
                                                        const expiryDate = cat.validToDate || cat.expiryDate ? new Date(cat.validToDate || cat.expiryDate) : null;
                                                        const isExpired = expiryDate && expiryDate <= now;
                                                        const isProvisional = cat.categoryType?.toLowerCase() === 'provisional';
                                                        const hasRestrictions = cat.restrictions && cat.restrictions.length > 0;
                                                        const warningDate = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
                                                        const isExpiringSoon = expiryDate && !isExpired && expiryDate <= warningDate;

                                                        let bgColor, textColor, borderColor, statusDot;
                                                        if (isExpired) { bgColor = 'bg-red-50'; textColor = 'text-red-700'; borderColor = 'border-red-200'; statusDot = <span className="w-1.5 h-1.5 rounded-full bg-red-500" />; }
                                                        else if (isProvisional) { bgColor = 'bg-yellow-50'; textColor = 'text-yellow-700'; borderColor = 'border-yellow-200'; statusDot = <span className="w-1.5 h-1.5 rounded-full bg-yellow-500" />; }
                                                        else if (hasRestrictions) { bgColor = 'bg-orange-50'; textColor = 'text-orange-700'; borderColor = 'border-orange-200'; statusDot = <span className="w-1.5 h-1.5 rounded-full bg-orange-500" />; }
                                                        else if (isProfessional) { bgColor = 'bg-green-50'; textColor = 'text-green-700'; borderColor = 'border-green-200'; statusDot = <span className="w-1.5 h-1.5 rounded-full bg-green-500" />; }
                                                        else { bgColor = 'bg-gray-50'; textColor = 'text-gray-500'; borderColor = 'border-gray-200'; statusDot = null; }

                                                        const expiryColorClass = isExpired ? 'text-red-400' : isExpiringSoon ? 'text-yellow-400' : 'text-green-400';
                                                        const statusText = isExpired ? 'Expired' : isProvisional ? 'Provisional' : hasRestrictions ? 'Restricted' : isProfessional ? 'Full' : '';

                                                        return (
                                                            <div key={idx} className="group relative">
                                                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border cursor-default ${bgColor} ${textColor} ${borderColor}`}>
                                                                    {statusDot}
                                                                    {cat.categoryCode}
                                                                </span>
                                                                <div className={`absolute left-1/2 -translate-x-1/2 bottom-full mb-2 hidden group-hover:block p-2 bg-gray-900 text-white text-xs rounded shadow-lg z-20 pointer-events-none ${hasRestrictions ? 'w-56' : 'w-36'}`}>
                                                                    <div className="font-medium text-center">{categoryNames[cat.categoryCode] || cat.categoryCode}</div>
                                                                    {statusText && <div className={`text-xs mt-1 text-center ${isExpired ? 'text-red-300' : isProvisional ? 'text-yellow-300' : hasRestrictions ? 'text-orange-300' : 'text-green-300'}`}>{statusText}</div>}
                                                                    {expiryDate && <div className={`text-xs mt-1 text-center ${expiryColorClass}`}>{isExpired ? 'Expired: ' : isExpiringSoon ? 'Expires: ' : 'Valid until: '}{expiryDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</div>}
                                                                    {hasRestrictions && cat.restrictions?.map((r: any, i: number) => (
                                                                        <div key={i} className="mt-1 text-orange-300"><span className="font-medium">{r.restrictionCode}</span>{r.restrictionLiteral && <span className="text-gray-300"> - {r.restrictionLiteral}</span>}</div>
                                                                    ))}
                                                                    <div className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                                                                </div>
                                                            </div>
                                                        );
                                                    };

                                                    return (
                                                        <div className="space-y-3">
                                                            {professionalCats.length > 0 && (
                                                                <div className="flex items-center gap-2">
                                                                    <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide whitespace-nowrap">{getProfessionalLabel()}</span>
                                                                    <div className="h-px bg-gray-200 w-4" />
                                                                    <div className="flex flex-wrap items-center gap-1.5">
                                                                        {professionalCats.map((cat, idx) => renderCategoryBadge(cat, idx, true))}
                                                                    </div>
                                                                </div>
                                                            )}
                                                            {standardCats.length > 0 && (
                                                                <details>
                                                                    <summary className="cursor-pointer list-none flex items-center gap-2 text-xs font-medium text-gray-400 uppercase tracking-wide whitespace-nowrap hover:text-gray-600">
                                                                        <svg className="w-3 h-3 text-gray-400 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                                                        </svg>
                                                                        Standard: {standardCats.length} categories
                                                                    </summary>
                                                                    <div className="flex flex-wrap items-center gap-1.5 mt-2 ml-5">
                                                                        {standardCats.map((cat, idx) => renderCategoryBadge(cat, idx, false))}
                                                                    </div>
                                                                </details>
                                                            )}
                                                        </div>
                                                    );
                                                })() : (
                                                    <span className="text-gray-500 text-sm">None specified</span>
                                                )}
                                            </div>
                                        </div>

                                        {/* Risk Assessment */}
                                        <div className="bg-gray-50/80 rounded-lg border border-gray-100 p-4">
                                            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Risk Assessment <span className="normal-case font-normal">(from latest check)</span></h3>

                                            {/* Score bar */}
                                            <div className="flex items-center gap-3 mb-1">
                                                <span className="text-xs font-medium text-gray-500 whitespace-nowrap">Risk Score</span>
                                                <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                                                    <div
                                                        className={`h-full rounded-full transition-all ${checkResult.riskAssessment.level === 'high' ? 'bg-red-500' : checkResult.riskAssessment.level === 'medium' ? 'bg-yellow-500' : 'bg-green-500'}`}
                                                        style={{ width: `${Math.min(checkResult.riskAssessment.score, 100)}%` }}
                                                    />
                                                </div>
                                                <div className="flex items-center gap-2 flex-shrink-0">
                                                    <span className={`text-sm font-bold ${checkResult.riskAssessment.level === 'high' ? 'text-red-600' : checkResult.riskAssessment.level === 'medium' ? 'text-yellow-600' : 'text-green-600'}`}>
                                                        {Math.min(checkResult.riskAssessment.score, 100)}/100
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="text-xs text-gray-400 mb-3">
                                                Next check due: {new Date(checkResult.riskAssessment.nextCheckDue).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                                            </div>

                                            {/* Risk factors */}
                                            {checkResult.riskAssessment.factors.length > 0 && (
                                                <div className="border-t border-gray-100 pt-3 mt-1">
                                                    <label className="block text-xs font-medium text-gray-500 mb-2">Risk Factors</label>
                                                    <ul className="space-y-1.5">
                                                        {checkResult.riskAssessment.factors.map((factor: any, index: number) => {
                                                            const isObject = typeof factor === 'object' && factor !== null;
                                                            const factorObj = isObject ? factor as { text: string; score: number; severity: string } : null;
                                                            const text = factorObj ? factorObj.text : (factor as string);
                                                            const severity = factorObj ? factorObj.severity : (text.startsWith('CRITICAL:') ? 'critical' : text.startsWith('URGENT:') ? 'urgent' : 'warning');
                                                            let displayText = text;
                                                            if (text.includes('Professional category restrictions:')) displayText = 'Professional categories have restrictions - see details below';
                                                            displayText = displayText.replace(/^(CRITICAL|URGENT):\s*/i, '');
                                                            const isCritical = severity === 'critical';
                                                            const isUrgent = severity === 'urgent';
                                                            const isInfo = severity === 'info';
                                                            const borderColor = isCritical ? 'border-red-400' : isUrgent ? 'border-orange-400' : isInfo ? 'border-gray-300' : 'border-yellow-400';
                                                            const textColor = isCritical ? 'text-red-700' : isUrgent ? 'text-orange-700' : isInfo ? 'text-gray-500' : 'text-gray-700';
                                                            const labelColor = isCritical ? 'text-red-500 font-bold' : isUrgent ? 'text-orange-500 font-semibold' : '';
                                                            return (
                                                                <li key={index} className={`text-sm pl-3 border-l-2 ${borderColor} ${textColor}`}>
                                                                    {(isCritical || isUrgent) && <span className={`text-xs mr-1 ${labelColor}`}>{isCritical ? 'CRITICAL:' : 'URGENT:'}</span>}
                                                                    {displayText}
                                                                </li>
                                                            );
                                                        })}
                                                    </ul>
                                                </div>
                                            )}

                                            {/* Recommendations */}
                                            {checkResult.riskAssessment.recommendations.length > 0 && (
                                                <div className="border-t border-gray-100 pt-3 mt-3">
                                                    <label className="block text-xs font-medium text-gray-500 mb-2">Recommendations</label>
                                                    <ul className="space-y-1.5">
                                                        {checkResult.riskAssessment.recommendations.map((rec: string, index: number) => (
                                                            <li key={index} className="text-sm pl-3 border-l-2 border-green-400 text-gray-700">{rec}</li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            )}
                                        </div>

                                        {/* Professional Driver Details */}
                                        {(checkResult.tachographDetails || checkResult.cpcDetails) && (
                                            <div className="bg-white border border-gray-200 rounded-lg p-6">
                                                <details className="group">
                                                    <summary className="cursor-pointer list-none">
                                                        <div className="flex items-center justify-between">
                                                            <h3 className="text-base font-semibold text-gray-900 flex items-center">
                                                                <CreditCard className="w-5 h-5 mr-2 text-gray-600" />
                                                                Professional Driver Details
                                                            </h3>
                                                            <div className="flex items-center space-x-2">
                                                                <span className="text-sm text-gray-500">
                                                                    {subtype === 'bus' ? (
                                                                        checkResult.cpcDetails?.length ? 'CPC on record' : 'No CPC data'
                                                                    ) : (subtype === 'coach' || subtype === 'haulage') ? (
                                                                        `${checkResult.cpcDetails?.length ? 'CPC' : 'No CPC'}${checkResult.tachographDetails?.length ? ` • ${checkResult.tachographDetails.length} tacho` : ' • No tacho'}`
                                                                    ) : (
                                                                        `${checkResult.cpcDetails?.length ? 'CPC' : ''}${checkResult.cpcDetails?.length && checkResult.tachographDetails?.length ? ' • ' : ''}${checkResult.tachographDetails?.length ? `${checkResult.tachographDetails.length} tacho` : ''}` || 'No professional data'
                                                                    )}
                                                                </span>
                                                                <div className="transform transition-transform group-open:rotate-180">
                                                                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                                                    </svg>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </summary>

                                                    <div className="mt-4 space-y-4 pl-7">
                                                        {checkResult.tachographDetails && checkResult.tachographDetails.length > 0 && (
                                                            <div>
                                                                <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
                                                                    <CreditCard className="w-4 h-4 mr-2" />Tachograph Cards
                                                                </h4>
                                                                <div className="space-y-3">
                                                                    {checkResult.tachographDetails.map((card: any, index: number) => (
                                                                        <div key={index} className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                                                            <div className="grid grid-cols-2 gap-4">
                                                                                <div>
                                                                                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide">Card Number</label>
                                                                                    <div className="mt-1 font-mono text-sm font-medium text-gray-900">{card.cardNumber}</div>
                                                                                </div>
                                                                                <div>
                                                                                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide">Status</label>
                                                                                    <div className="mt-1">
                                                                                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${['VALID', 'DISPATCHED', 'RENEWED'].includes(card.cardStatus) ? 'bg-green-100 text-green-800' : card.cardStatus === 'EXPIRED' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'}`}>
                                                                                            {card.cardStatus}
                                                                                        </span>
                                                                                    </div>
                                                                                </div>
                                                                                <div>
                                                                                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide">Valid From</label>
                                                                                    <div className="mt-1 text-sm text-gray-900">{new Date(card.cardStartOfValidityDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</div>
                                                                                </div>
                                                                                <div>
                                                                                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide">Expires</label>
                                                                                    <div className="mt-1 text-sm">
                                                                                        <span className={`${new Date(card.cardExpiryDate) < new Date() ? 'text-red-600 font-medium' : new Date(card.cardExpiryDate) < new Date(Date.now() + 90 * 24 * 60 * 60 * 1000) ? 'text-yellow-600 font-medium' : 'text-gray-900'}`}>
                                                                                            {new Date(card.cardExpiryDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                                                                                            {new Date(card.cardExpiryDate) < new Date() && <span className="ml-2 text-xs">(EXPIRED)</span>}
                                                                                            {new Date(card.cardExpiryDate) >= new Date() && new Date(card.cardExpiryDate) < new Date(Date.now() + 90 * 24 * 60 * 60 * 1000) && <span className="ml-2 text-xs">(EXPIRES SOON)</span>}
                                                                                        </span>
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        )}

                                                        {subtype !== 'car_fleet' && checkResult.cpcDetails && (
                                                            <div>
                                                                <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
                                                                    <Calendar className="w-4 h-4 mr-2" />
                                                                    {subtype === 'bus' || subtype === 'coach' ? 'Driver CPC (Passenger)' : subtype === 'haulage' ? 'Driver CPC (Goods)' : 'Certificate of Professional Competence (CPC)'}
                                                                </h4>
                                                                <div className="border rounded-lg p-4" style={{ backgroundColor: 'rgba(105, 190, 40, 0.1)', borderColor: 'rgba(105, 190, 40, 0.3)' }}>
                                                                    <div className="grid grid-cols-2 gap-4">
                                                                        {checkResult.cpcDetails.map((cpc: any, index: number) => (
                                                                            <React.Fragment key={index}>
                                                                                {cpc.lgvValidTo && (subtype === 'haulage' || subtype === 'general') && (
                                                                                    <div>
                                                                                        <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide">LGV (Goods) Valid To</label>
                                                                                        <div className="mt-1 text-sm text-gray-900">{new Date(cpc.lgvValidTo).toLocaleDateString('en-GB')}</div>
                                                                                    </div>
                                                                                )}
                                                                                {cpc.pcvValidTo && (subtype === 'bus' || subtype === 'coach' || subtype === 'general') && (
                                                                                    <div>
                                                                                        <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide">PCV (Passenger) Valid To</label>
                                                                                        <div className="mt-1 text-sm text-gray-900">{new Date(cpc.pcvValidTo).toLocaleDateString('en-GB')}</div>
                                                                                    </div>
                                                                                )}
                                                                            </React.Fragment>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}

                                                        {subtype !== 'car_fleet' && (() => {
                                                            const relevantCodes = subtype === 'bus' || subtype === 'coach' ? pcvCodes : subtype === 'haulage' ? hgvCodes : [...pcvCodes, ...hgvCodes];
                                                            const profCats = checkResult.categories.filter((cat: any) => relevantCodes.includes(cat.categoryCode));
                                                            if (profCats.length === 0) return (
                                                                <div className="text-sm text-gray-500 italic">
                                                                    {subtype === 'bus' || subtype === 'coach' ? 'No PCV categories (D/D1) found on licence' : subtype === 'haulage' ? 'No HGV categories (C/CE) found on licence' : 'No professional driving categories found'}
                                                                </div>
                                                            );
                                                            const now = new Date();
                                                            return (
                                                                <div>
                                                                    <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
                                                                        <Shield className="w-4 h-4 mr-2" />
                                                                        {subtype === 'bus' || subtype === 'coach' ? 'PCV Category Details' : subtype === 'haulage' ? 'HGV Category Details' : 'Professional Category Details'}
                                                                    </h4>
                                                                    <div className="border rounded-lg overflow-hidden">
                                                                        <table className="w-full text-sm">
                                                                            <thead className="border-b border-gray-100 bg-gray-50/95">
                                                                                <tr>
                                                                                    <th className="px-4 py-3 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Category</th>
                                                                                    <th className="px-4 py-3 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                                                                                    <th className="px-4 py-3 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Valid To</th>
                                                                                    <th className="px-4 py-3 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Restrictions</th>
                                                                                </tr>
                                                                            </thead>
                                                                            <tbody className="divide-y">
                                                                                {profCats.map((cat: any, index: number) => {
                                                                                    const isExpired = cat.expiryDate && new Date(cat.expiryDate) <= now;
                                                                                    const isProvisional = cat.categoryType?.toLowerCase() === 'provisional';
                                                                                    const hasRestrictions = cat.restrictions && cat.restrictions.length > 0;
                                                                                    const statusBadge = isExpired
                                                                                        ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700"><XCircle className="w-3 h-3" /> Expired</span>
                                                                                        : isProvisional
                                                                                        ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700"><AlertTriangle className="w-3 h-3" /> Provisional</span>
                                                                                        : hasRestrictions
                                                                                        ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-700"><AlertCircle className="w-3 h-3" /> Restricted</span>
                                                                                        : <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700"><CheckCircle2 className="w-3 h-3" /> Full</span>;
                                                                                    return (
                                                                                        <tr key={index} className={isExpired ? 'bg-red-50' : ''}>
                                                                                            <td className="px-3 py-2 font-semibold">{cat.categoryCode}</td>
                                                                                            <td className="px-3 py-2">{statusBadge}</td>
                                                                                            <td className="px-3 py-2 text-gray-600">{cat.expiryDate ? new Date(cat.expiryDate).toLocaleDateString('en-GB') : '-'}</td>
                                                                                            <td className="px-3 py-2">
                                                                                                {hasRestrictions && cat.restrictions ? (
                                                                                                    <div className="space-y-0.5">
                                                                                                        {cat.restrictions.map((r: any, i: number) => (
                                                                                                            <div key={i} className="text-xs text-orange-700">
                                                                                                                <span className="font-medium">{r.restrictionCode}</span>
                                                                                                                {r.restrictionLiteral && <span className="text-gray-500"> - {r.restrictionLiteral}</span>}
                                                                                                            </div>
                                                                                                        ))}
                                                                                                    </div>
                                                                                                ) : <span className="text-gray-400">None</span>}
                                                                                            </td>
                                                                                        </tr>
                                                                                    );
                                                                                })}
                                                                            </tbody>
                                                                        </table>
                                                                    </div>
                                                                </div>
                                                            );
                                                        })()}

                                                        {subtype !== 'car_fleet' && checkResult.tachographDetails?.length > 0 && (
                                                            <div className="flex items-center justify-between bg-gray-50 rounded-lg px-4 py-3">
                                                                <span className="text-sm font-medium text-gray-700">Digital Tachograph Card</span>
                                                                <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${checkResult.tachographDetails.some((card: any) => ['VALID', 'DISPATCHED', 'RENEWED'].includes(card.cardStatus)) ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                                                    {checkResult.tachographDetails.some((card: any) => ['VALID', 'DISPATCHED', 'RENEWED'].includes(card.cardStatus)) ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                                                                    {checkResult.tachographDetails[0].cardStatus}
                                                                </span>
                                                            </div>
                                                        )}
                                                    </div>
                                                </details>
                                            </div>
                                        )}

                                        {/* Endorsements */}
                                        {checkResult.endorsements.length > 0 && (() => {
                                            const now = new Date();
                                            now.setHours(0, 0, 0, 0);
                                            const isDisqualified = checkResult.status?.toLowerCase().includes('disqualified') || checkResult.status?.toLowerCase().includes('revoked');
                                            const isPointsExpired = (e: any) => {
                                                if (!e.penaltyPointsExpiryDate) return false;
                                                const d = new Date(e.penaltyPointsExpiryDate);
                                                d.setHours(0, 0, 0, 0);
                                                return d < now;
                                            };
                                            const isSeriousOffence = (code: string) => {
                                                if (!code) return false;
                                                return ['DR', 'DD', 'CD', 'UT', 'BA', 'IN10'].some(p => code.toUpperCase().startsWith(p));
                                            };
                                            const activePoints = checkResult.endorsements.reduce((sum: number, e: any) => (!e.penaltyPoints || isPointsExpired(e)) ? sum : sum + e.penaltyPoints, 0);
                                            const sorted = [...checkResult.endorsements].sort((a: any, b: any) => new Date(b.convictionDate).getTime() - new Date(a.convictionDate).getTime());
                                            const hasSeriousOffence = sorted.some((e: any) => isSeriousOffence(e.offenceCode));
                                            const hasActiveDisqualification = sorted.some((e: any) => {
                                                if (!e.disqualification?.startDate) return false;
                                                const start = new Date(e.disqualification.startDate);
                                                const end = new Date(start);
                                                end.setFullYear(end.getFullYear() + (e.disqualification.years || 0));
                                                end.setMonth(end.getMonth() + (e.disqualification.months || 0));
                                                return end > now;
                                            });
                                            const pointsUntilBan = Math.max(0, 12 - activePoints);
                                            const pointsPercentage = Math.min(100, (activePoints / 12) * 100);

                                            const endorsementCard = (endorsement: any, index: number) => {
                                                const expired = isPointsExpired(endorsement);
                                                const serious = isSeriousOffence(endorsement.offenceCode);
                                                const hasPoints = endorsement.penaltyPoints && endorsement.penaltyPoints > 0;
                                                return (
                                                    <div key={index} className={`border rounded-lg p-4 ${expired || !hasPoints ? 'bg-gray-50 border-gray-200' : 'bg-orange-50 border-orange-200'}`}>
                                                        <div className="flex items-start justify-between gap-4">
                                                            <div className="flex-1 min-w-0">
                                                                <div className="flex items-center gap-2 flex-wrap">
                                                                    <span className={`font-semibold ${expired || !hasPoints ? 'text-gray-500' : 'text-gray-900'}`}>{endorsement.offenceCode}</span>
                                                                    {serious && <span className="px-1.5 py-0.5 text-xs font-medium bg-red-100 text-red-700 rounded">Serious</span>}
                                                                    {expired && hasPoints && <span className="px-1.5 py-0.5 text-xs font-medium bg-gray-200 text-gray-600 rounded">Expired</span>}
                                                                </div>
                                                                <p className={`text-sm mt-1 ${expired || !hasPoints ? 'text-gray-500' : 'text-gray-600'}`}>{endorsement.offenceLegalLiteral}</p>
                                                                <p className="text-xs text-gray-400 mt-2">
                                                                    {new Date(endorsement.convictionDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                                                                    {endorsement.penaltyPointsExpiryDate && <span className="ml-2">· Points {expired ? 'expired' : 'expire'} {new Date(endorsement.penaltyPointsExpiryDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</span>}
                                                                </p>
                                                                {endorsement.disqualification && (
                                                                    <div className="mt-2 flex items-center gap-1.5 text-xs text-red-600">
                                                                        <AlertCircle className="w-3.5 h-3.5" />
                                                                        <span>Disqualified{endorsement.disqualification.years > 0 && ` ${endorsement.disqualification.years} year${endorsement.disqualification.years !== 1 ? 's' : ''}`}{endorsement.disqualification.months > 0 && ` ${endorsement.disqualification.months} month${endorsement.disqualification.months !== 1 ? 's' : ''}`}{endorsement.disqualification.startDate && ` from ${new Date(endorsement.disqualification.startDate).toLocaleDateString('en-GB')}`}</span>
                                                                    </div>
                                                                )}
                                                            </div>
                                                            <div className="flex-shrink-0">
                                                                {hasPoints ? (
                                                                    <div className={`text-center min-w-[48px] px-2 py-1 rounded-lg ${expired ? 'bg-gray-200 text-gray-500' : 'bg-orange-500 text-white'}`}>
                                                                        <div className={`text-lg font-bold ${expired ? 'line-through' : ''}`}>{endorsement.penaltyPoints}</div>
                                                                        <div className="text-xs opacity-75">pts</div>
                                                                    </div>
                                                                ) : (
                                                                    <div className="text-center min-w-[48px] px-2 py-1 rounded-lg bg-gray-100 text-gray-400">
                                                                        <div className="text-lg font-bold">-</div>
                                                                        <div className="text-xs">pts</div>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            };

                                            if (isDisqualified || hasActiveDisqualification) {
                                                return (
                                                    <details className="group bg-white border border-gray-200 rounded-lg overflow-hidden">
                                                        <summary className="cursor-pointer list-none p-4 hover:bg-gray-50">
                                                            <div className="flex items-center justify-between">
                                                                <div className="flex items-center text-sm">
                                                                    <div className="transform transition-transform group-open:rotate-180 mr-2">
                                                                        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                                                                    </div>
                                                                    <h4 className="font-medium text-gray-700">Driving Record ({sorted.length} endorsement{sorted.length !== 1 ? 's' : ''})</h4>
                                                                </div>
                                                                {hasSeriousOffence && <span className="px-2 py-1 text-xs font-medium bg-amber-100 text-amber-700 rounded">Serious offence</span>}
                                                            </div>
                                                        </summary>
                                                        <div className="px-4 pb-4 space-y-3">{sorted.map(endorsementCard)}</div>
                                                    </details>
                                                );
                                            }

                                            return (
                                                <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                                                    {activePoints >= 9 && (
                                                        <div className="bg-red-600 text-white px-4 py-3 flex items-center gap-2">
                                                            <AlertTriangle className="w-5 h-5 flex-shrink-0" />
                                                            <span className="font-medium">{activePoints >= 12 ? 'At or over 12 points - driver may face disqualification' : `Warning: ${pointsUntilBan} points until automatic ban`}</span>
                                                        </div>
                                                    )}
                                                    <div className="p-6">
                                                        <div className="mb-6">
                                                            <div className="flex items-center justify-between mb-2">
                                                                <h3 className="text-lg font-semibold text-gray-900">Penalty Points</h3>
                                                                <div className="text-right">
                                                                    <span className={`text-2xl font-bold ${activePoints >= 9 ? 'text-red-600' : activePoints >= 6 ? 'text-orange-500' : 'text-green-600'}`}>{activePoints}</span>
                                                                    <span className="text-gray-400 text-lg">/12</span>
                                                                </div>
                                                            </div>
                                                            <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                                                                <div className={`h-full transition-all duration-300 ${activePoints >= 9 ? 'bg-red-500' : activePoints >= 6 ? 'bg-orange-400' : 'bg-green-500'}`} style={{ width: `${pointsPercentage}%` }} />
                                                            </div>
                                                            <p className="text-sm text-gray-500 mt-1">
                                                                {activePoints === 0 ? (hasSeriousOffence ? 'No active penalty points - but has offence history' : 'No active penalty points') : `${pointsUntilBan} points until automatic disqualification`}
                                                            </p>
                                                        </div>
                                                        {hasSeriousOffence && (
                                                            <div className="mb-4 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-2">
                                                                <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0" />
                                                                <span className="text-sm text-amber-800">Driver has serious offence(s) on record - may affect insurance</span>
                                                            </div>
                                                        )}
                                                        <details className="group">
                                                            <summary className="cursor-pointer list-none">
                                                                <div className="flex items-center text-sm">
                                                                    <div className="transform transition-transform group-open:rotate-180 mr-2">
                                                                        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                                                                    </div>
                                                                    <h4 className="font-medium text-gray-700">{sorted.length} endorsement{sorted.length !== 1 ? 's' : ''} on record</h4>
                                                                </div>
                                                            </summary>
                                                            <div className="mt-3 space-y-3">{sorted.map(endorsementCard)}</div>
                                                        </details>
                                                    </div>
                                                </div>
                                            );
                                        })()}

                                        {/* Restrictions */}
                                        {checkResult.restrictions.length > 0 && (
                                            <div className="bg-white border border-gray-200 rounded-lg p-6">
                                                <h3 className="text-base font-semibold text-gray-900 mb-4 flex items-center">
                                                    <AlertCircle className="w-5 h-5 mr-2 text-gray-600" />Restrictions
                                                </h3>
                                                <ul className="space-y-2">
                                                    {checkResult.restrictions.map((restriction: string, index: number) => (
                                                        <li key={index} className="text-sm text-gray-700 flex items-center gap-2">
                                                            <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                                                            <span>{restriction}</span>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}

                                    </div>
                                )}
                            </div>
                        )}

                        {/* ── Documents ── */}
                        {activeTab === 'documents' && (
                            <DriverDocumentsTab
                                driverId={driver.id}
                                canManage={user?.role === 'admin' || user?.role === 'manager' || user?.role === 'super_admin'}
                                onDriverUpdated={onDriverUpdated}
                            />
                        )}

                        {/* ── History ── */}
                        {activeTab === 'history' && (() => {
                            const isLoading = isLoadingHistory || isLoadingConsentTokens;

                            // Build unified event list
                            type HistoryEvent =
                                | { kind: 'licence'; date: Date; check: LicenceCheckResult; isLatest: boolean }
                                | { kind: 'consent'; date: Date; token: ConsentStatusResponse['tokens'][number]; isLatest: boolean };

                            const sortedConsent = [...(consentTokens ?? [])].sort(
                                (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
                            );

                            const events: HistoryEvent[] = [
                                ...checkHistory.map((check, i) => ({
                                    kind: 'licence' as const,
                                    date: new Date(check.checkDate),
                                    check,
                                    isLatest: i === 0,
                                })),
                                ...sortedConsent.map((token, i) => ({
                                    kind: 'consent' as const,
                                    date: new Date(token.createdAt),
                                    token,
                                    isLatest: i === 0,
                                })),
                            ].sort((a, b) => b.date.getTime() - a.date.getTime());

                            const filtered = historyFilter === 'all' ? events : events.filter(e => e.kind === historyFilter);

                            const licenceCount = events.filter(e => e.kind === 'licence').length;
                            const consentCount = events.filter(e => e.kind === 'consent').length;

                            return (
                                <div className="p-4 space-y-4">
                                    {/* Header + filters */}
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <h3 className="text-sm font-semibold text-gray-900">Compliance Timeline</h3>
                                            <div className="flex items-center gap-2 mt-0.5">
                                                <span className="text-xs text-gray-400">{licenceCount} licence check{licenceCount !== 1 ? 's' : ''}</span>
                                                <span className="text-gray-200 text-xs">|</span>
                                                <span className="text-xs text-gray-400">{consentCount} consent request{consentCount !== 1 ? 's' : ''}</span>
                                            </div>
                                        </div>
                                        <div className="flex gap-1">
                                            {(['all', 'licence', 'consent'] as const).map(f => (
                                                <button
                                                    key={f}
                                                    onClick={() => setHistoryFilter(f)}
                                                    className={`px-3 py-1 text-xs font-medium rounded-full border transition-colors ${
                                                        historyFilter === f
                                                            ? 'text-white border-transparent'
                                                            : 'bg-white text-gray-500 border-gray-200 hover:border-gray-300'
                                                    }`}
                                                    style={historyFilter === f ? { backgroundColor: '#69BE28', borderColor: '#69BE28' } : {}}
                                                >
                                                    {f.charAt(0).toUpperCase() + f.slice(1)}
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    {isLoading ? (
                                        <div className="flex items-center justify-center py-12">
                                            <Loader2 className="w-5 h-5 animate-spin mr-2" style={{ color: '#69BE28' }} />
                                            <span className="text-sm text-gray-600">Loading history...</span>
                                        </div>
                                    ) : filtered.length === 0 ? (
                                        <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                                            <Clock className="w-8 h-8 mb-2" />
                                            <p className="text-sm">No events found.</p>
                                        </div>
                                    ) : (
                                        <div className="rounded-lg border border-gray-100 overflow-hidden">
                                            <table className="min-w-full divide-y divide-gray-100">
                                                <thead className="border-b border-gray-100 bg-gray-50/95">
                                                    <tr>
                                                        <th className="px-4 py-3 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Type</th>
                                                        <th className="px-4 py-3 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Date</th>
                                                        <th className="px-4 py-3 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                                                        <th className="px-4 py-3 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Risk</th>
                                                        <th className="px-4 py-3 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Details</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="bg-white divide-y divide-gray-100">
                                                    {filtered.map((event) => {
                                                        if (event.kind === 'licence') {
                                                            const { check, isLatest } = event;
                                                            const pts = check.status?.toLowerCase().includes('disqualified') || check.status?.toLowerCase().includes('revoked')
                                                                ? null : check.penaltyPoints ?? 0;
                                                            return (
                                                                <tr key={`l-${check.id}`} className="hover:bg-gray-50/60 transition-colors">
                                                                    <td className="px-4 py-2.5">
                                                                        <div className="flex items-center gap-1.5">
                                                                            <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${isLatest ? 'bg-green-400' : 'invisible'}`} title={isLatest ? 'Latest' : undefined} />
                                                                            <span className="inline-flex items-center gap-2 whitespace-nowrap text-xs font-semibold text-gray-700"><span className="h-4 w-[3px] rounded-full bg-blue-500" aria-hidden="true" />Licence</span>
                                                                        </div>
                                                                    </td>
                                                                    <td className="px-4 py-2.5 text-sm text-gray-700 whitespace-nowrap">{formatDateTime(check.checkDate)}</td>
                                                                    <td className="px-4 py-2.5">{(() => { const sb = getHistoryStatusBar(check.status, check.valid); return <span className="inline-flex items-center gap-2 whitespace-nowrap text-xs font-semibold text-gray-700"><span className={`h-4 w-[3px] rounded-full ${sb.barClass}`} aria-hidden="true" />{sb.label}</span>; })()}</td>
                                                                    <td className="px-4 py-2.5">{(() => { const rb = getHistoryRiskBar(check.riskAssessment?.level || check.riskLevel); return <span className="inline-flex items-center gap-2 whitespace-nowrap text-xs font-semibold text-gray-700"><span className={`h-4 w-[3px] rounded-full ${rb.barClass}`} aria-hidden="true" />{rb.label}</span>; })()}</td>
                                                                    <td className="px-4 py-2.5">
                                                                        <div className="flex items-center gap-1.5">
                                                                            {check.isAutomated && <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-xs font-medium bg-purple-50 text-purple-700"><Bot className="w-3 h-3" />Auto</span>}
                                                                            <span className="text-xs text-gray-400">{pts === null ? <span className="text-red-500">N/A pts</span> : `${pts} pts`}</span>
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            );
                                                        } else {
                                                            const { token, isLatest } = event;
                                                            return (
                                                                <tr key={`c-${token.id}`} className="hover:bg-gray-50/60 transition-colors">
                                                                    <td className="px-4 py-2.5">
                                                                        <div className="flex items-center gap-1.5">
                                                                            <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${isLatest ? 'bg-green-400' : 'invisible'}`} title={isLatest ? 'Latest' : undefined} />
                                                                            <span className="inline-flex items-center gap-2 whitespace-nowrap text-xs font-semibold text-gray-700"><span className="h-4 w-[3px] rounded-full bg-amber-400" aria-hidden="true" />Consent</span>
                                                                        </div>
                                                                    </td>
                                                                    <td className="px-4 py-2.5 text-sm text-gray-700 whitespace-nowrap">{formatDateTime(token.createdAt)}</td>
                                                                    <td className="px-4 py-2.5">{(() => { const cb = getConsentStatusBar(token.status); return <span className="inline-flex items-center gap-2 whitespace-nowrap text-xs font-semibold text-gray-700"><span className={`h-4 w-[3px] rounded-full ${cb.barClass}`} aria-hidden="true" />{cb.label}</span>; })()}</td>
                                                                    <td className="px-4 py-2.5 text-xs text-gray-400">-</td>
                                                                    <td className="px-4 py-2.5 text-xs text-gray-400">
                                                                        {token.usedAt ? (
                                                                            <div>
                                                                                <div className="text-gray-500 font-medium">Signed</div>
                                                                                <div>{formatDateTime(token.usedAt)}</div>
                                                                            </div>
                                                                        ) : '-'}
                                                                    </td>
                                                                </tr>
                                                            );
                                                        }
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                            );
                        })()}

                        {/* ── Consent ── */}
                        {activeTab === 'consent' && (() => {
                            const isConsentExpiredLocal = !!(driver.consentExpiry && new Date(driver.consentExpiry) < new Date());
                            const hasPendingRequest = driver.consentStatus?.tokens?.some(
                                (t: any) => t.status === 'sent' || t.status === 'opened'
                            );
                            const canSendNew = (!driver.consentProvided || isConsentExpiredLocal) && !hasPendingRequest;
                            const isActive = driver.consentProvided && !isConsentExpiredLocal;

                            return (
                                <div className="p-4 space-y-4">

                                    {/* Status strip */}
                                    <div className={cardClass}>
                                        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Consent Status</h3>
                                        <div className={`flex items-center gap-2 px-3 py-2.5 rounded-lg ${isActive ? 'bg-green-600' : isConsentExpiredLocal ? 'bg-red-600' : 'bg-amber-500'}`}>
                                            {isActive
                                                ? <CheckCircle2 className="w-4 h-4 text-white flex-shrink-0" />
                                                : <AlertTriangle className="w-4 h-4 text-white flex-shrink-0" />
                                            }
                                            <span className="text-sm font-bold text-white">
                                                {isActive ? 'D906 Consent Active' : isConsentExpiredLocal ? 'D906 Consent Expired' : 'D906 Consent Not Provided'}
                                            </span>
                                        </div>
                                    </div>

                                    {/* Details */}
                                    <div className={`${cardClass} space-y-3`}>
                                        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Consent Details</h3>
                                        <Row icon={<Calendar className="w-4 h-4 text-gray-400" />} label="Consent Date" value={formatDate(driver.consentDate)} />
                                        <Row icon={<Calendar className="w-4 h-4 text-gray-400" />} label="Consent Expiry" value={formatDate(driver.consentExpiry)} />
                                        {driver.consentReference && (
                                            <Row icon={<FileText className="w-4 h-4 text-gray-400" />} label="Reference" value={<span className="font-mono text-xs">{driver.consentReference}</span>} />
                                        )}
                                        {driver.consentSentDate && <Row icon={<Mail className="w-4 h-4 text-gray-400" />} label="Last Request Sent" value={formatDate(driver.consentSentDate)} />}
                                        {driver.consentSentBy && <Row icon={<User className="w-4 h-4 text-gray-400" />} label="Sent By" value={driver.consentSentBy} />}
                                        {driver.consentReminderCount != null && driver.consentReminderCount > 0 && (
                                            <Row icon={<MessageSquare className="w-4 h-4 text-gray-400" />} label="Reminders Sent" value={String(driver.consentReminderCount)} />
                                        )}
                                    </div>

                                    {/* Actions */}
                                    {driver.active && (
                                        <div className={`${cardClass} space-y-3`}>
                                            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</h3>

                                            <div className="flex flex-wrap gap-2">
                                                {canSendNew && (
                                                    <button
                                                        onClick={handleSendDigitalConsent}
                                                        disabled={isSendingConsent}
                                                        className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-white rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50"
                                                        style={{ backgroundColor: '#4F46E5' }}
                                                    >
                                                        {isSendingConsent ? <><Loader2 className="w-4 h-4 animate-spin" />Sending...</> : <><Send className="w-4 h-4" />Send Digital Consent Form</>}
                                                    </button>
                                                )}
                                                {canSendNew && !showRecordOffline && (
                                                    <button
                                                        onClick={() => setShowRecordOffline(true)}
                                                        className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                                                    >
                                                        <FileCheck className="w-4 h-4" />Record Offline Consent
                                                    </button>
                                                )}
                                                {hasPendingRequest && !canSendNew && (
                                                    <button
                                                        onClick={handleSendReminder}
                                                        disabled={isSendingReminder}
                                                        className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-white rounded-lg hover:opacity-90 disabled:opacity-50"
                                                        style={{ backgroundColor: '#69BE28' }}
                                                    >
                                                        {isSendingReminder ? <><Loader2 className="w-4 h-4 animate-spin" />Sending...</> : <><Mail className="w-4 h-4" />Send Reminder</>}
                                                    </button>
                                                )}
                                                {isActive && (
                                                    <button
                                                        onClick={handleWithdrawConsent}
                                                        disabled={isWithdrawingConsent}
                                                        className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50"
                                                    >
                                                        {isWithdrawingConsent ? 'Processing...' : 'Withdraw Consent'}
                                                    </button>
                                                )}
                                            </div>

                                            {canSendNew && showRecordOffline && (
                                                <div className="rounded-lg border border-gray-200 p-3 space-y-2.5">
                                                    <p className="text-xs font-medium text-gray-600">Enter the D906 form reference number</p>
                                                    <input
                                                        type="text"
                                                        value={consentReference}
                                                        onChange={e => setConsentReference(e.target.value)}
                                                        placeholder="e.g. D906-2024-001234"
                                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-1"
                                                        style={{ outlineColor: '#69BE28' }}
                                                        autoFocus
                                                    />
                                                    <div className="flex gap-2">
                                                        <button
                                                            onClick={() => { setShowRecordOffline(false); setConsentReference(''); }}
                                                            className="px-3 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                                                        >
                                                            Cancel
                                                        </button>
                                                        <button
                                                            onClick={handleSubmitOfflineConsent}
                                                            disabled={isRecordingConsent || !consentReference.trim()}
                                                            className="px-3 py-2 text-sm font-medium text-white rounded-lg hover:opacity-90 disabled:opacity-50"
                                                            style={{ backgroundColor: '#69BE28' }}
                                                        >
                                                            {isRecordingConsent ? 'Recording...' : 'Record Consent'}
                                                        </button>
                                                    </div>
                                                </div>
                                            )}

                                            {canSendNew && <p className="text-xs text-gray-400">Use "Record Offline Consent" only if the driver signed a physical D906 form</p>}
                                        </div>
                                    )}
                                </div>
                            );
                        })()}

                        {/* ── DBS ── */}
                        {activeTab === 'dbs' && user?.organisation?.trustidEnabled && (
                            <DbsTab
                                driver={driver}
                                orgName={orgName}
                                isAdmin={isAdmin}
                                currentUserName={currentUserName}
                                onDriverUpdated={onDriverUpdated}
                                isDemoMode={user?.organisation?.demoMode === true}
                            />
                        )}

                        {/* ── Communications ── */}
                        {activeTab === 'communications' && (
                            <div className="flex flex-col h-full">
                                {isLoadingEmails && (
                                    <div className="flex items-center justify-center flex-1">
                                        <div className="animate-spin h-6 w-6 border-2 border-t-transparent rounded-full" style={{ borderColor: '#69BE28 transparent transparent transparent' }} />
                                    </div>
                                )}
                                {emailError && (
                                    <div className="m-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center justify-between flex-shrink-0">
                                        <p className="text-sm text-red-800">{emailError}</p>
                                        <button onClick={loadEmailHistory} className="px-3 py-1 text-xs font-medium text-white bg-red-600 rounded-lg hover:bg-red-700">Retry</button>
                                    </div>
                                )}
                                {emailHistory && !isLoadingEmails && (
                                    <div className="flex flex-col flex-1 min-h-0 p-6 gap-4">
                                        <div className="grid grid-cols-4 gap-3 flex-shrink-0">
                                            {[
                                                { label: 'Total',     value: emailHistory.data.summary.totalEmails,       bg: 'bg-gray-50',   border: 'border-gray-200',  text: 'text-gray-900'  },
                                                { label: 'Delivered', value: emailHistory.data.summary.successfulEmails,  bg: 'bg-green-50',  border: 'border-green-200', text: 'text-green-700' },
                                                { label: 'Pending',   value: emailHistory.data.summary.pendingEmails,     bg: 'bg-amber-50',  border: 'border-amber-200', text: 'text-amber-700' },
                                                { label: 'Failed',    value: emailHistory.data.summary.failedEmails,      bg: 'bg-red-50',    border: 'border-red-200',   text: 'text-red-600'   },
                                            ].map(s => (
                                                <div key={s.label} className={`${s.bg} border ${s.border} rounded-lg p-3 text-center`}>
                                                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">{s.label}</p>
                                                    <p className={`text-2xl font-bold ${s.text}`}>{s.value}</p>
                                                </div>
                                            ))}
                                        </div>
                                        {emailHistory.data.emailHistory && emailHistory.data.emailHistory.length > 0 ? (
                                            <div className="flex flex-col flex-1 min-h-0 gap-2">
                                                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider flex-shrink-0">Email Log</h4>
                                                <div className="flex-1 overflow-y-auto space-y-2">
                                                    {emailHistory.data.emailHistory.map((log) => (
                                                        <div key={log.id} className="flex items-center justify-between bg-gray-50/80 border border-gray-100 rounded-lg px-4 py-3">
                                                            <div>
                                                                <p className="text-sm font-medium text-gray-900 capitalize">{log.emailType.replace(/_/g, ' ')}</p>
                                                                <p className="text-xs text-gray-500 mt-0.5">
                                                                    {new Date(log.sentAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                                                                    {log.retryCount > 0 && <span className="ml-2 text-orange-500">{log.retryCount} {log.retryCount === 1 ? 'retry' : 'retries'}</span>}
                                                                </p>
                                                            </div>
                                                            <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${log.deliveryStatus === 'delivered' ? 'bg-green-100 text-green-800' : log.deliveryStatus === 'bounced' ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-800'}`}>
                                                                {log.deliveryStatus.charAt(0).toUpperCase() + log.deliveryStatus.slice(1)}
                                                            </span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="flex-1 flex flex-col items-center justify-center text-center">
                                                <MessageSquare className="w-10 h-10 text-gray-300 mb-3" />
                                                <p className="text-sm text-gray-500">No emails sent to this driver yet.</p>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Sticky footer - licence actions */}
                    {activeTab === 'licence' && (
                        <div className="flex-shrink-0 flex items-center justify-between px-4 py-3 border-t border-gray-100 bg-gray-50/80">
                            <div className="flex items-center gap-2 text-sm text-gray-500">
                                {isLoadingLatest ? (
                                    <span>Loading check data...</span>
                                ) : checkResult ? (
                                    <>
                                        <Clock className="w-4 h-4 flex-shrink-0" />
                                        <span>Last DVLA check: {formatDateTime(checkResult.checkDate)}</span>
                                    </>
                                ) : (
                                    <span className="text-gray-400">No DVLA check on record</span>
                                )}
                            </div>
                            {driver.active && (
                                <button
                                    onClick={handleCheckButtonClick}
                                    disabled={isChecking || !driver.consentProvided || isConsentExpired()}
                                    className="inline-flex items-center gap-2 px-4 py-2 text-white rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                                    style={{ backgroundColor: '#69BE28' }}
                                >
                                    {isChecking
                                        ? <><Loader2 className="w-4 h-4 animate-spin" /><span>Checking DVLA...</span></>
                                        : <><RefreshCw className="w-4 h-4" /><span>Perform New Check</span></>
                                    }
                                </button>
                            )}
                        </div>
                    )}

                </div>
            </div>

            {/* Recent check confirmation overlay */}
            {showRecentCheckConfirm && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-[60]">
                    <div className="bg-white rounded-xl max-w-md w-full shadow-2xl">
                        <div className="p-6">
                            <div className="flex items-start gap-3 mb-4">
                                <div className="p-2 bg-yellow-100 rounded-lg flex-shrink-0">
                                    <AlertTriangle className="w-5 h-5 text-yellow-600" />
                                </div>
                                <div>
                                    <h3 className="text-base font-semibold text-gray-900">Driver Recently Checked</h3>
                                    <p className="text-sm text-gray-600 mt-1">
                                        {driver.firstName} {driver.lastName} was last checked on{' '}
                                        <span className="font-medium">{formatDate(driver.lastCheckDate!)}</span>{' '}
                                        ({driver.daysSinceLastCheck} {driver.daysSinceLastCheck === 1 ? 'day' : 'days'} ago).
                                    </p>
                                    <p className="text-sm text-gray-600 mt-2">
                                        Running another check so soon may use an additional DVLA credit. Do you want to continue?
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="flex gap-3 px-6 pb-6">
                            <button
                                onClick={() => setShowRecentCheckConfirm(false)}
                                className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={performLicenceCheck}
                                className="flex-1 px-4 py-2 text-sm font-medium text-white rounded-lg hover:opacity-90 transition-opacity"
                                style={{ backgroundColor: '#69BE28' }}
                            >
                                Check Anyway
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <EditDriverModal
                isOpen={showEditDriverModal}
                onClose={() => setShowEditDriverModal(false)}
                onSubmit={handleEditDriverSubmit}
                isUpdating={isUpdatingDriver}
                driver={driver}
            />

        </>
    );
};

export default DriverViewModal;
